import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { UserContext } from "../../../context/UserContext";
import apiFetch, { safeJson, apiErrorDetail } from "../../../context/api";
import LocationChoiceModal from "../DispenserComponents/LocationChoiceModal";
import { esFuenteDeVenta } from "../../../lib/helpers/locations";
import ImpresoraBarra from "./ImpresoraBarra";
import useImpresoraComandas from "../../hooks/useImpresoraComandas";
import { guardarVistas, leerVistas } from "../../../lib/impresion";
import BasculaModal from "./BasculaModal";
import EditarComandaModal from "./EditarComandaModal";
import ConfirmDialog from "../../Elements/ConfirmDialog";
import Button from "../../Elements/Button";
import SectionHeader from "../../Elements/SectionHeader";
import { esPeso, cantidadLegible, cantidadDespachada, hayBascula, notaPedidoMerma } from "../../../lib/helpers/products";
import useMermaTolerancia from "../../../lib/hooks/useMermaTolerancia";

/**
 * Pantalla de Barra (CRUO F1) — cola COMPARTIDA de comandas.
 *
 * Es "solo una URL": cualquier tablet/móvil del staff la abre. Las dos barras ven
 * TODAS las comandas. Polling corto (sin websockets, ARQUITECTURA_CARRITO_V2.md §2.3)
 * + aviso sonoro cuando entra comanda nueva.
 *
 * UN SOLO PASO (petición del club, 17-ago): "Servida ✓" confirma preparación y entrega
 * de una vez — dar a "Preparar" antes era un toque que no aportaba nada en una barra
 * donde casi todo es bebida. `preparing` sigue existiendo: es donde están las comandas
 * de antes de este cambio, y una tablet con el bundle viejo cacheado aún lo manda.
 */

const POLL_MS = 5000;

// El estado con los tonos semánticos del sistema (tokens de aviso de tokens.css):
// pendiente = aviso, preparando = info, completada = éxito, cancelada = error. Antes
// cuatro paletas crudas (ámbar/cian/esmeralda/rosa) elegidas a mano (CRUO-007).
const ESTADOS = {
  pending:   { label: "Pendiente",  next: "completed", accion: "Servida ✓", tone: "bg-[var(--toast-warning-bg)] text-[var(--toast-warning-text)] border-[var(--toast-warning-border)]" },
  preparing: { label: "Preparando", next: "completed", accion: "Servida ✓", tone: "bg-[var(--toast-info-bg)] text-[var(--toast-info-text)] border-[var(--toast-info-border)]" },
  completed: { label: "Completada", next: null,        accion: null,        tone: "bg-[var(--toast-success-bg)] text-[var(--toast-success-text)] border-[var(--toast-success-border)]" },
  cancelled: { label: "Cancelada",  next: null,        accion: null,        tone: "bg-[var(--toast-error-bg)] text-[var(--toast-error-text)] border-[var(--toast-error-border)]" },
};

function beep() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.frequency.value = 880;
    gain.gain.setValueAtTime(0.15, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.4);
    osc.start();
    osc.stop(ctx.currentTime + 0.4);
  } catch {
    /* sin audio no pasa nada */
  }
}

function edadMinutos(iso) {
  if (!iso) return null;
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  return mins < 1 ? "ahora" : `hace ${mins} min`;
}

const mismoId = (a, b) => String(a || "").toLowerCase() === String(b || "").toLowerCase();

function ComandaCard({ order, onAdvance, onCancel, onEdit, onReimprimir, busy, fallo, onCerrarFallo }) {
  const meta = ESTADOS[order.status] || ESTADOS.pending;
  const esServe = order.fulfillment === "serve";
  return (
    /* La tarjeta del sistema: trazo de 1 px y sin sombra. Una comanda pendiente se
       distingue por el trazo en el tono de aviso (y por su chip), no por un borde
       doble ámbar. */
    <div className={`rounded-lg border p-4 bg-[var(--card)] ${order.status === "pending" ? "border-[var(--toast-warning-border)]" : "border-[var(--border)]"}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="font-semibold text-[var(--foreground)] break-words">
            {order.customer_name || "Socio"}
            <span className="ml-2 text-xs font-normal text-[var(--muted-foreground)]">#{String(order.id).slice(0, 8)}</span>
          </div>
          <div className="mt-0.5 text-sm">
            {esServe ? (
              <span className="font-medium text-[var(--foreground)]">
                🍽️{" "}
                {order.table_display_name
                  ? `Servir en ${order.table_display_name}`
                  : order.table_label
                    ? `Servir en ${order.table_label}`
                    : "Servir en mesa"}
                {/* Con nombre propio, la referencia impresa va al lado: es lo que
                    se lee en la pegatina de la mesa (plan CRUO §G). */}
                {order.table_display_name && order.table_label ? (
                  <span className="ml-1 text-xs font-normal text-[var(--muted-foreground)]">({order.table_label})</span>
                ) : null}
              </span>
            ) : (
              <span className="font-medium text-[var(--foreground)]">🛎️ Recoge en barra</span>
            )}
            {/* `whitespace-nowrap`: es una sola cosa que leer de un vistazo. Sin esto,
                en la tarjeta estrecha de una tablet en vertical partía por la mitad y
                dejaba la «m» de «min» sola en la línea de abajo (reporte 25-08). Y al
                mismo tamaño que la línea en la que vive, que si no parece letra pequeña. */}
            <span className="ml-2 whitespace-nowrap text-sm text-[var(--muted-foreground)]">{edadMinutos(order.created_at)}</span>
          </div>
        </div>
        <span className={`whitespace-nowrap rounded-full border px-2.5 py-0.5 text-xs font-semibold ${meta.tone}`}>
          {meta.label}
        </span>
      </div>

      <ul className="mt-3 space-y-1 border-t border-[var(--border)] pt-2">
        {(order.items || []).map((it) => (
          <li key={it.id} className="flex justify-between gap-2 text-sm">
            {/* `min-w-0` + `break-words`: el nombre no se recorta nunca (petición CRUO 24-08),
                pero un nombre largo de una pieza tampoco puede desbordar la tarjeta. */}
            <span className="min-w-0 break-words">
              {/* Los productos por peso llevan GRAMOS en `quantity` (el precio es
                  €/g): "250×" le decía a la barra que sirviera 250 lonchas. El
                  predicado es el compartido, para que carta y barra no puedan
                  discrepar sobre la misma línea. */}
              <b className="text-[var(--foreground)]">
                {esPeso(it) ? `${cantidadLegible(cantidadDespachada(it))} g` : `${cantidadLegible(it.quantity)}×`}
              </b>{" "}
              {it.product_name}
              {/* Báscula ya apuntada (desde el POS de Sala o al servir): visible en
                  la tarjeta, no solo dentro del modal — «los datos que se hayan
                  introducido» a la vista (petición CRUO 23-08). */}
              {esPeso(it) && hayBascula(it) ? (
                <span className="ml-1 text-xs text-[var(--muted-foreground)]">· ⚖ {notaPedidoMerma(it) || 'pesado'}</span>
              ) : null}
              {it.notes ? <span className="ml-1 text-xs italic text-[var(--muted-foreground)]">({it.notes})</span> : null}
            </span>
            <span className="flex-shrink-0 tabular-nums text-[var(--muted-foreground)]">{Number(it.subtotal).toFixed(2)}&nbsp;€</span>
          </li>
        ))}
      </ul>

      {/* El motivo del fallo vive EN LA TARJETA que se acaba de tocar (no en un aviso
          arriba de la página, fuera de pantalla en una tablet) y NO lo borra el polling:
          se va cuando la barra lo cierra o vuelve a intentarlo. */}
      {fallo && (
        <div
          data-testid={`fallo-${order.id}`}
          className="mt-3 flex items-start justify-between gap-2 rounded-lg border border-[var(--toast-error-border)] bg-[var(--toast-error-bg)] px-3 py-2 text-xs text-[var(--toast-error-text)]"
        >
          <span>{fallo}</span>
          <button
            type="button"
            onClick={() => onCerrarFallo(order)}
            aria-label="Cerrar aviso"
            className="leading-none opacity-60 hover:opacity-100"
          >
            ×
          </button>
        </div>
      )}

      {/* Envolver, no desbordar: al añadir el botón de reimprimir pasamos de tres
          controles a cuatro y "Servida ✓" se salía por la derecha en columna estrecha. */}
      <div className="mt-3 flex flex-wrap items-center justify-between gap-2 border-t border-[var(--border)] pt-2">
        {/* El importe que se va a cobrar, no el de tarifa. `order.total` lleva SOLO
            el descuento manual de línea; el del socio lo aplica el cobro al servir,
            así que la barra cantaba 2,50 € a un socio al 50% al que se le quitaban
            1,25 € del monedero (reportado por el club, 01-09-2026). El backend
            manda `total_a_cobrar` calculado con las mismas funciones que cobran.
            Se tacha el de tarifa para que el camarero pueda explicar la diferencia. */}
        <span className="flex items-baseline gap-1.5 text-sm font-bold text-[var(--foreground)]">
          {order.total_a_cobrar != null && Number(order.total_a_cobrar) !== Number(order.total) ? (
            <>
              <span className="font-normal text-[var(--muted-foreground)] line-through">
                {Number(order.total).toFixed(2)}&nbsp;€
              </span>
              <span>{Number(order.total_a_cobrar).toFixed(2)}&nbsp;€</span>
              {order.member_discount_label && (
                <span className="rounded-full bg-[var(--toast-success-bg)] px-2 py-0.5 text-xs font-medium text-[var(--success)]">
                  socio {order.member_discount_label}
                </span>
              )}
            </>
          ) : (
            <span>{Number(order.total).toFixed(2)}&nbsp;€</span>
          )}
        </span>
        {/* Los botones del sistema (Button, 36 px): «Servida ✓» primario, «Editar» y
            reimprimir outline, «Cancelar» ghost en destructivo (el `!` porque el color de
            ghost se genera después en el CSS de Tailwind y pisa el destructivo). */}
        <div className="flex flex-wrap justify-end gap-2">
          {/* También en "preparando": una comanda que no se puede cobrar (sin stock, por
              ejemplo) se quedaba sin ninguna salida — su único botón fallaba siempre. */}
          {(order.status === "pending" || order.status === "preparing") && (
            <>
              <Button variant="ghost" className="!text-[var(--destructive)]" disabled={busy} onClick={() => onCancel(order)}>
                Cancelar
              </Button>
              {/* Editar: la salida del callejón «4 pedidas, 3 en stock» — la comanda
                  se corrige aquí y se sirve, en vez de quedarse atascada (plan CRUO §C). */}
              {onEdit && (
                <Button variant="outline" disabled={busy} onClick={() => onEdit(order)}>
                  Editar
                </Button>
              )}
            </>
          )}
          {/* Solo donde hay impresora: para el ticket que se rasgó mal o se perdió. */}
          {onReimprimir && (
            <Button
              variant="outline"
              onClick={() => onReimprimir(order)}
              aria-label="Reimprimir ticket"
              title="Reimprimir ticket"
            >
              🖨️
            </Button>
          )}
          {meta.next && (
            <Button disabled={busy} onClick={() => onAdvance(order, meta.next)}>
              {meta.accion}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

export default function BarraPage() {
  const [token, , , venueID, , , , , , , , venueType] = useContext(UserContext);
  const [orders, setOrders] = useState([]);
  const [error, setError] = useState("");
  const [busyId, setBusyId] = useState(null);
  // Motivo del último fallo POR COMANDA. Aparte de `error` (fallos de carga) a propósito:
  // `cargar` limpia `error` cada 5 s y se llevaba por delante el motivo del cobro.
  const [fallos, setFallos] = useState({});
  // 409 `location_choice_required`: el cobro necesita saber de qué nevera sale una línea.
  // { order, nuevo, detail, choices } — al elegir se reintenta el PATCH con la elección.
  const [locationChoice, setLocationChoice] = useState(null);
  // U0 · comandas a peso: el 400 weights_required abre la báscula.
  const [pesaje, setPesaje] = useState(null);
  // Tolerancia de báscula del club (Configuración; default 0,10 g) — para el modal.
  const toleranciaG = useMermaTolerancia();
  const [verCompletadas, setVerCompletadas] = useState(false);
  // Comanda a la espera de que la barra confirme la cancelación (ver `cancelar`).
  const [pendienteCancelar, setPendienteCancelar] = useState(null);
  // Comanda abierta en el editor (plan CRUO §C) y las mesas del club para el
  // selector de entrega. Las mesas se piden la primera vez que hace falta — un
  // club sin mesas no gana un GET en cada carga de Barra.
  const [editando, setEditando] = useState(null);
  const [mesas, setMesas] = useState(null);
  // Ids ya vistos → el aviso y el ticket solo saltan con los nuevos. Se guardan EN EL
  // DISPOSITIVO, no solo en memoria: iOS purga la página al pasar la app a segundo
  // plano y al volver se recarga sola. Con el conjunto en memoria, las comandas que
  // llegaron mientras tanto se tomaban por "ya estaban ahí" y no se imprimían nunca.
  const conocidasRef = useRef(new Set());
  // Solo el estreno de verdad —cuando no hay nada guardado— se calla: es el único
  // momento en que avisar de todo lo pendiente sería escupir el histórico entero.
  const estrenoRef = useRef(false);


  const authHeaders = useMemo(
    () => ({ Authorization: `Bearer ${token}`, "X-Venue-Id": venueID || "" }),
    [token, venueID]
  );

  // Un fallo de impresión se cuenta EN LA TARJETA, igual que los del cobro: mismo
  // lenguaje visual y misma × para cerrarlo. Nunca sube ni interrumpe la cola.
  const impresora = useImpresoraComandas(venueID, {
    onFallo: (order, motivo) => setFallos((f) => ({ ...f, [order.id]: motivo })),
    authHeaders,
  });
  const { revisarCola, reintentarEnSilencio, sincronizarEstado, refrescarEstacion } = impresora;

  // Antes que el sondeo: este efecto está declarado arriba a propósito, para que la
  // memoria esté cargada cuando `cargar` corra por primera vez.
  useEffect(() => {
    const vistas = leerVistas(venueID);
    conocidasRef.current = new Set(vistas);
    estrenoRef.current = vistas.length === 0;
  }, [venueID]);

  const cargar = useCallback(async () => {
    if (!token || !venueID) return;
    try {
      const res = await apiFetch(`/v1/orders?limit=100`, { headers: authHeaders });
      if (!res.ok) {
        const detail = (await res.json().catch(() => null))?.detail;
        throw new Error(typeof detail === "string" ? detail : "No se pudieron cargar las comandas");
      }
      const data = await safeJson(res);
      // Beep solo si aparece una comanda pendiente que no habíamos visto.
      const nuevasPendientes = (data || []).filter(
        (o) => o.status === "pending" && !conocidasRef.current.has(o.id)
      );
      // Solo el estreno se calla. A partir de ahí, "nueva" significa que no está en la
      // memoria del dispositivo — y eso sobrevive a que iOS recargue la página.
      const esEstreno = estrenoRef.current;
      estrenoRef.current = false;
      if (!esEstreno && nuevasPendientes.length > 0) beep();
      const antes = conocidasRef.current.size;
      for (const o of data || []) conocidasRef.current.add(o.id);
      // Escribir solo cuando cambia: el sondeo es cada 5 s y esto no necesita ir a
      // disco en cada vuelta.
      if (conocidasRef.current.size !== antes) {
        guardarVistas(venueID, Array.from(conocidasRef.current));
      }
      // El chip se refresca contra la conexión REAL en cada vuelta: fiarse solo del
      // evento de desconexión lo dejaba verde con la impresora muerta.
      sincronizarEstado();
      // Quién imprime: latimos si la tenemos, y si no, nos enteramos de quién la tiene.
      refrescarEstacion();
      // Qué falta por imprimir lo dice el SERVIDOR, no este aparato: se repasa la cola
      // entera en cada vuelta, así entran también las reimpresiones que pide otra
      // pantalla y lo que quedó sin salir cuando la impresora estaba caída.
      revisarCola(data || []);
      // Reconexión silenciosa colgada del sondeo que ya existía: sin temporizador nuevo.
      reintentarEnSilencio();
      setOrders(data || []);
      setError("");
    } catch (e) {
      setError(e.message || "Error cargando comandas");
    }
  }, [token, venueID, authHeaders, revisarCola, reintentarEnSilencio, sincronizarEstado, refrescarEstacion]);

  useEffect(() => {
    cargar();
    const t = setInterval(cargar, POLL_MS);
    return () => clearInterval(t);
  }, [cargar]);

  const cambiarEstado = useCallback(
    async (order, nuevo, choices = [], weights = []) => {
      // Comanda con líneas a peso rumbo a cobro: la báscula se abre SIEMPRE antes
      // del PATCH, pre-cargada con lo apuntado en el POS de Sala (real_weight) y
      // editable — no solo cuando el 400 weights_required obligaba. Un peso
      // enviado en item_weights SOBRESCRIBE el precargado en el backend; el 400
      // sigue de red de seguridad por si esta rama no cubre algún camino.
      if (nuevo === "completed" && !(weights || []).length) {
        const aPeso = (order.items || []).filter((it) => esPeso(it));
        // Espejo de _cobra_al_completar: sin socio, sin importe o en un bistro
        // el backend NO cobra (ni valida pesos) — abrir la báscula ahí era una
        // puerta que el servidor nunca pidió y que bloqueaba el "un toque"
        // (hallazgo code review 23-08).
        const cobra = order.customer_id != null && Number(order.total) > 0 && venueType !== "bistro";
        if (aPeso.length > 0 && cobra) {
          // La pregunta de nevera se resuelve ANTES de abrir («elimina la primera
          // pantalla», CRUO 23-08): se consulta el stock por ubicación y, si algún
          // producto vive en 2+ ubicaciones de sala con stock, la sección
          // «¿De dónde sale?» sale YA en el primer y único modal. La regla es el
          // espejo de decide_sale_location (sala = no-principal y dispensable,
          // ambigua con 2+ con stock); si esta réplica se queda corta (sala
          // estricta, dispensario, fallo de red), el 409 sigue de red de seguridad
          // y el modal reaparece con la pregunta — nunca se pierde una venta.
          setBusyId(order.id);
          let ubicacion = null;
          try {
            // Una consulta por PRODUCTO, no por línea (una comanda con la misma
            // birra en 3 líneas no merece 3 GETs).
            const vistos = new Set();
            const unicos = (order.items || []).filter((it) => {
              if (vistos.has(it.product_id)) return false;
              vistos.add(it.product_id);
              return true;
            });
            const respuestas = await Promise.all(
              unicos.map((it) =>
                apiFetch(`/v1/stock/by-location?venue_id=${venueID}&product_id=${it.product_id}`, { headers: authHeaders })
                  .then((r) => (r.ok ? r.json() : null))
                  .then((d) => ({ it, d }))
                  .catch(() => ({ it, d: null }))
              )
            );
            for (const { it, d } of respuestas) {
              const sala = (d?.by_location || []).filter((l) => esFuenteDeVenta(l));
              const needed = Number(it.real_weight ?? it.quantity) || 0;
              const opciones = sala.map((l) => ({
                location_id: l.location_id,
                name: l.location_name,
                kind: l.kind,
                quantity: l.quantity,
                enough: Number(l.quantity) >= needed,
              }));
              // Solo se pregunta si hay algo PULSABLE (mismo guard que el 409):
              // sin ninguna opción que llegue, el modal saldría entero apagado y
              // la comanda sin salida — mejor dejar que el 409 lo explique con
              // su mensaje (hallazgo code review 23-08).
              if (sala.length >= 2 && opciones.some((o) => o.enough)) {
                ubicacion = {
                  product_id: it.product_id,
                  product_name: it.product_name,
                  reason: "ambiguous",
                  options: opciones,
                };
                break; // una pregunta por vez, como el backend
              }
            }
          } catch {
            // sin datos de ubicación: el modal abre solo con báscula y el 409 responde
          } finally {
            setBusyId(null);
          }
          setPesaje({
            order,
            nuevo,
            choices,
            ubicacion,
            lineas: aPeso.map((it) => ({
              order_item_id: it.id,
              product_id: it.product_id,
              quantity: it.quantity,
              product_name: it.product_name,
              real_weight: it.real_weight,
            })),
          });
          return; // sigue en `confirmarPesos`
        }
      }
      setBusyId(order.id);
      setFallos((f) => ({ ...f, [order.id]: undefined }));
      try {
        const res = await apiFetch(`/v1/orders/${order.id}/status`, {
          method: "PATCH",
          headers: { ...authHeaders, "Content-Type": "application/json" },
          body: JSON.stringify({ status: nuevo, item_locations: choices, item_weights: weights }),
        });
        if (!res.ok) {
          // El `detail` puede ser texto ("Stock insuficiente: …"), un objeto (409 de
          // ubicación) o la lista de errores de validación de FastAPI (422): `apiErrorDetail`
          // es el que sabe leer los tres, así que la respuesta entera va con él.
          const data = await res.json().catch(() => null);
          const detail = data?.detail;
          // "Servida ✓" cobra del monedero: si el producto está en 2+ neveras de sala, el
          // backend no adivina y pregunta. Antes esto no tenía respuesta posible desde la
          // barra y la comanda se quedaba atascada de por vida.
          if (res.status === 409 && detail && typeof detail === "object" && detail.code === "location_choice_required") {
            const opciones = Array.isArray(detail.options) ? detail.options : [];
            // Si ya contestamos por este producto y vuelven a preguntar, la elección no se
            // está aplicando (backend sin `item_locations` aún). Reabrir el diálogo sería un
            // bucle: el trabajador elegiría nevera una y otra vez sin llegar a servir nada.
            if ((choices || []).some((c) => mismoId(c.product_id, detail.product_id))) {
              // Ya contestamos por este producto y vuelven a preguntar. Dos motivos muy
              // distintos: que la nevera elegida se haya quedado sin stock entre la pregunta
              // y el cobro (elección caducada, no bucle — el siguiente toque vuelve a
              // preguntar desde cero), o que la elección no se esté aplicando.
              if (detail.reason === "insufficient_at_location") {
                throw new Error(
                  `En la nevera elegida ya no queda suficiente ${detail.product_name || "producto"}. ` +
                    "Vuelve a darle a Servida y elige otra."
                );
              }
              throw new Error(
                `No se pudo aplicar la ubicación elegida para ${detail.product_name || "el producto"}. ` +
                  "Deja el stock en una sola nevera o cancela la comanda."
              );
            }
            // Abrir el diálogo solo si hay algo que se pueda pulsar: las opciones sin stock
            // suficiente salen deshabilitadas. Con 2× CERVEZA repartidas 1+1 entre dos
            // neveras, NINGUNA llega y el modal saldría entero apagado — otra vez la comanda
            // sin salida. Mejor decir por qué.
            if (opciones.some((o) => o.enough)) {
              const aPeso = (order.items || []).filter((it) => esPeso(it));
              if (aPeso.length > 0) {
                // Comanda a peso: la pregunta de nevera se contesta DENTRO de
                // «Pesar antes de servir», no en un segundo popup encadenado
                // («esos 2 en el mismo modal», petición CRUO 23-08). Los pesos
                // recién confirmados vuelven pre-cargados.
                const pesoDe = new Map((weights || []).map((w) => [String(w.order_item_id), w.real_weight]));
                setPesaje({
                  order,
                  nuevo,
                  choices,
                  ubicacion: detail,
                  lineas: aPeso.map((it) => ({
                    order_item_id: it.id,
                    product_id: it.product_id,
                    quantity: it.quantity,
                    product_name: it.product_name,
                    real_weight: pesoDe.get(String(it.id)) ?? it.real_weight,
                  })),
                });
                return; // sigue en `confirmarPesos` (pesos + nevera de un toque)
              }
              // `weights` viaja también: sin esto, el desvío por ubicación tiraba
              // los pesos recién confirmados y la báscula se pedía dos veces.
              setLocationChoice({ order, nuevo, detail, choices, weights });
              return; // sigue en `elegirUbicacion`
            }
            const producto = detail.product_name || "el producto";
            throw new Error(
              opciones.length === 0
                ? `No hay ninguna ubicación con stock de ${producto}`
                : `En ninguna ubicación hay suficiente ${producto}: el stock está repartido ` +
                  "entre varias. Júntalo en una o cancela la comanda."
            );
          }
          // U0 · comandas a peso: el cobro exige la báscula de cada línea a peso.
          // Se abre el modal con LAS LÍNEAS QUE FALTAN (el 400 las trae) y al
          // confirmar se reintenta con item_weights. Cerrar sin pesar deja la
          // comanda viva y lo cuenta — el mismo contrato que las ubicaciones.
          if (res.status === 400 && detail && typeof detail === "object" && detail.code === "weights_required") {
            const porId = new Map((order.items || []).map((it) => [String(it.id), it]));
            const lineas = (detail.items || []).map((li) => ({
              ...li,
              product_name: porId.get(String(li.order_item_id))?.product_name,
            }));
            if ((weights || []).length > 0) {
              // Ya mandamos pesos y sigue pidiendo: algo no aplica — no reabrir en bucle.
              throw new Error("No se pudieron aplicar los pesos de báscula. Vuelve a darle a Servida ✓.");
            }
            setPesaje({ order, nuevo, choices, lineas });
            return; // sigue en `confirmarPesos`
          }
          if (res.status === 400 && detail && typeof detail === "object" && detail.code === "weight_out_of_tolerance") {
            // El servidor manda (el FE ya avisa antes, pero la báscula pudo cambiar):
            // merma fuera de ±0,10 g — se dice con las dos cifras y no se maquilla.
            throw new Error(
              `Merma fuera de rango: cobrados ${detail.quantity} g, báscula ${detail.real_weight} g ` +
                `(tolerancia ±${detail.tolerance_g} g). Pesa de nuevo y vuelve a darle a Servida ✓.`
            );
          }
          throw new Error(apiErrorDetail(data, "No se pudo actualizar la comanda"));
        }
        await cargar();
      } catch (e) {
        setFallos((f) => ({ ...f, [order.id]: e.message || "No se pudo actualizar la comanda" }));
      } finally {
        setBusyId(null);
      }
    },
    [authHeaders, cargar, venueID, venueType]
  );

  /** Cerrar el diálogo sin elegir (× o fondo) deja la comanda EXACTAMENTE igual que antes del
   *  toque: sin decirlo, la tarjeta vuelve a su aspecto normal y nadie sabe que el cobro se
   *  intentó y se quedó a medias. */
  const cerrarEleccion = useCallback(() => {
    const pendiente = locationChoice;
    setLocationChoice(null);
    if (!pendiente) return;
    setFallos((f) => ({
      ...f,
      [pendiente.order.id]:
        "La comanda no se ha cobrado: falta decir de qué ubicación sale " +
        `${pendiente.detail?.product_name || "el producto"}. Vuelve a darle a Servida ✓.`,
    }));
  }, [locationChoice]);

  /** Cerrar la báscula sin pesar deja la comanda EXACTAMENTE igual: viva, sin
   *  cobrar, y la tarjeta lo cuenta para que nadie crea que se sirvió. */
  const cerrarPesaje = useCallback(() => {
    const pendiente = pesaje;
    setPesaje(null);
    if (!pendiente) return;
    setFallos((f) => ({
      ...f,
      [pendiente.order.id]:
        "La comanda no se ha cobrado: falta pesar las líneas a peso. Vuelve a darle a Servida ✓.",
    }));
  }, [pesaje]);

  const confirmarPesos = useCallback(
    async (weights, locationId) => {
      const pendiente = pesaje;
      if (!pendiente) return;
      setPesaje(null);
      // Modo combinado: la nevera elegida en el propio modal se suma a las
      // respuestas de ubicación (misma forma que elegirUbicacion).
      let choices = pendiente.choices || [];
      if (locationId && pendiente.ubicacion?.product_id) {
        choices = [
          ...choices.filter((c) => !mismoId(c.product_id, pendiente.ubicacion.product_id)),
          { product_id: pendiente.ubicacion.product_id, location_id: locationId },
        ];
      }
      await cambiarEstado(pendiente.order, pendiente.nuevo, choices, weights);
    },
    [pesaje, cambiarEstado]
  );

  const elegirUbicacion = useCallback(
    async (locationId) => {
      const pendiente = locationChoice;
      if (!pendiente) return;
      // "Continuar sin elegir ubicación" (onChoose(null)) reintentaría con la misma pregunta
      // sin contestar → mismo 409, mismo diálogo. Aquí eso es cerrar, y se dice.
      if (!locationId) {
        cerrarEleccion();
        return;
      }
      setLocationChoice(null);
      const productId = pendiente.detail.product_id;
      const choices = [
        ...(pendiente.choices || []).filter((c) => !mismoId(c.product_id, productId)),
        { product_id: productId, location_id: locationId },
      ];
      await cambiarEstado(pendiente.order, pendiente.nuevo, choices, pendiente.weights || []);
    },
    [locationChoice, cambiarEstado, cerrarEleccion]
  );

  // Preguntar SIEMPRE, no solo en "preparando": con el paso único, "Cancelar" está pegado
  // a "Servida ✓" en toda comanda pendiente y cancelar no tiene vuelta atrás (una comanda
  // cancelada ya no muestra ningún botón).
  //
  // El diálogo es el de la app (`ConfirmDialog`), no `window.confirm`: en la tablet de la barra
  // ese diálogo del navegador se traga —basta con que alguien marque "impedir que esta página
  // cree más diálogos", y desde ese momento TODOS devuelven `false` en silencio hasta recargar—
  // y el botón se quedaba muerto sin decir nada ("no hace nada de nada", reporte CRUO 24-08).
  const cancelar = useCallback((order) => setPendienteCancelar(order), []);

  const abrirEditor = useCallback(
    async (order) => {
      setEditando(order);
      if (mesas === null) {
        try {
          const res = await apiFetch(`/v1/service-tables?venue_id=${venueID}`, { headers: authHeaders });
          setMesas(res.ok ? (await safeJson(res)) || [] : []);
        } catch {
          setMesas([]); // sin mesas el selector solo ofrece «Recoge en barra»
        }
      }
    },
    [mesas, venueID, authHeaders]
  );

  const guardarEdicion = useCallback(
    async (payload) => {
      const order = editando;
      if (!order) return;
      setBusyId(order.id);
      setFallos((f) => ({ ...f, [order.id]: undefined }));
      try {
        const res = await apiFetch(`/v1/orders/${order.id}`, {
          method: "PATCH",
          headers: { ...authHeaders, "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
        if (!res.ok) {
          const data = await res.json().catch(() => null);
          const detail = data?.detail;
          // Otra tablet la tocó entre que se abrió el editor y se guardó: se
          // recarga la cola y se pide volver a editar sobre lo último — nunca se
          // pisa a ciegas lo que hizo la otra pantalla.
          if (res.status === 409 && detail && typeof detail === "object" && detail.code === "stale_order") {
            setEditando(null);
            await cargar();
            throw new Error(detail.message || "Otra pantalla ha tocado esta comanda: vuelve a editarla.");
          }
          throw new Error(apiErrorDetail(data, "No se pudo editar la comanda"));
        }
        setEditando(null);
        await cargar();
      } catch (e) {
        setFallos((f) => ({ ...f, [order.id]: e.message || "No se pudo editar la comanda" }));
      } finally {
        setBusyId(null);
      }
    },
    [editando, authHeaders, cargar]
  );

  const confirmarCancelacion = useCallback(() => {
    const order = pendienteCancelar;
    setPendienteCancelar(null);
    if (order) cambiarEstado(order, "cancelled");
  }, [pendienteCancelar, cambiarEstado]);

  const cerrarFallo = useCallback((order) => {
    setFallos((f) => ({ ...f, [order.id]: undefined }));
  }, []);

  // Reimprimir solo donde ESTE aparato tiene la impresora. Ofrecer el botón en una tablet
  // que no la tiene es dar a elegir algo que siempre contesta con un error: en un club con
  // varias pantallas, ruido puro.
  // En el aparato con la impresora se reimprime en el acto; en los demás se PIDE, y lo
  // saca la estación en su sondeo. Antes esas pantallas no tenían salida ninguna.
  const puedeReimprimir = impresora.conectada
    ? impresora.reimprimir
    : (impresora.estacion?.activa ? impresora.pedirReimpresion : null);

  const activas = orders.filter((o) => o.status === "pending" || o.status === "preparing");
  const cerradas = orders.filter((o) => o.status === "completed" || o.status === "cancelled");
  const pendientes = activas.filter((o) => o.status === "pending").length;

  return (
    // `overflow-x-clip` y no `-hidden`: hidden convertía esta raíz en su propio
    // scroller y `main` dejaba de scrollear (lo mismo que la Sala, CRUO-004).
    <div className="flex max-w-full flex-col gap-4 overflow-x-clip p-4">
      {/* La cabecera del sistema (SectionHeader): título, contador de pendientes y la
          impresora como acción. Antes un `flex justify-between` sin wrap sobre
          `overflow-x-hidden`: a 390 el contador quedaba FUERA de la pantalla y el
          título se partía junto al aviso de impresora (CRUO-008). El contador dice
          «N pendientes» como el «N dentro» de Entradas, y en el tono normal: tener
          cola es el estado habitual de una barra, no una alerta (code-review). */}
      <SectionHeader
        title="Barra · Comandas"
        subtitle="Cola compartida — un toque sirve y cobra la comanda. Se actualiza sola."
        count={<span data-testid="pendientes-count">{pendientes} pendiente{pendientes === 1 ? "" : "s"}</span>}
        sticky={false}
        actionsClassName="w-full min-w-0 flex-col items-stretch sm:flex-row sm:items-center sm:justify-end lg:w-auto"
      >
          <ImpresoraBarra
            disponible={impresora.disponible}
            conectada={impresora.conectada}
            conectando={impresora.conectando}
            emparejada={impresora.emparejada}
            papel={impresora.papel}
            enCola={impresora.enCola}
            estacion={impresora.estacion}
            onConectar={impresora.conectar}
            onPrueba={impresora.imprimirPrueba}
            onOlvidar={impresora.olvidar}
          />
      </SectionHeader>

      {error && (
        <div className="rounded-lg border border-[var(--toast-error-border)] bg-[var(--toast-error-bg)] px-4 py-2 text-sm text-[var(--toast-error-text)]">
          {error}
        </div>
      )}

      {activas.length === 0 && !error && (
        <div className="rounded-lg border border-dashed border-[var(--border)] p-10 text-center text-[var(--muted-foreground)]">
          No hay comandas activas. ☕
        </div>
      )}

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
        {activas.map((o) => (
          <ComandaCard
            key={o.id}
            order={o}
            busy={busyId === o.id}
            fallo={fallos[o.id]}
            onCerrarFallo={cerrarFallo}
            onAdvance={cambiarEstado}
            onCancel={cancelar}
            onEdit={abrirEditor}
            onReimprimir={puedeReimprimir}
          />
        ))}
      </div>

      <div className="mt-6">
        <Button variant="ghost" size="sm" onClick={() => setVerCompletadas((v) => !v)}>
          {verCompletadas ? "Ocultar" : "Ver"} completadas de hoy ({cerradas.length})
        </Button>
        {verCompletadas && (
          <div className="mt-3 grid grid-cols-1 gap-3 opacity-70 md:grid-cols-2 xl:grid-cols-3">
            {cerradas.map((o) => (
              <ComandaCard key={o.id} order={o} busy onAdvance={() => {}} onCancel={() => {}} />
            ))}
          </div>
        )}
      </div>

      {/* "¿De dónde sale?" — mismo diálogo que usa el POS, para que la barra pueda
          contestar al 409 en vez de quedarse con la comanda muerta. */}
      <LocationChoiceModal
        active={!!locationChoice}
        detail={locationChoice?.detail}
        unitType={
          (locationChoice?.order?.items || []).find((it) =>
            mismoId(it.product_id, locationChoice?.detail?.product_id)
          )?.unit_type
        }
        onClose={cerrarEleccion}
        onChoose={elegirUbicacion}
      />

      {/* «Pesar antes de servir» — se abre al darle a Servida ✓ con líneas a peso
          (pre-cargado y editable) y sigue contestando al 400 weights_required. */}
      <BasculaModal
        active={!!pesaje}
        lineas={pesaje?.lineas || []}
        busy={busyId === pesaje?.order?.id}
        toleranciaG={toleranciaG}
        ubicacion={pesaje?.ubicacion || null}
        onClose={cerrarPesaje}
        onConfirm={confirmarPesos}
      />

      {/* Editar una comanda viva: cantidades, quitar líneas, mover la mesa. */}
      <EditarComandaModal
        active={!!editando}
        order={editando}
        mesas={mesas || []}
        busy={busyId === editando?.id}
        onClose={() => setEditando(null)}
        onSave={guardarEdicion}
      />

      {/* Cancelar una comanda no tiene vuelta atrás, así que se pregunta — pero con el diálogo
          de la app, que sí se ve en la tablet de la barra. Las etiquetas evitan el equívoco de
          tener dos botones «Cancelar» que significan lo contrario. */}
      <ConfirmDialog
        active={!!pendienteCancelar}
        title="¿Cancelar esta comanda?"
        description="No se cobrará nada y no se puede deshacer."
        tone="danger"
        confirmLabel="Sí, cancelar la comanda"
        cancelLabel="No, volver"
        onConfirm={confirmarCancelacion}
        onCancel={() => setPendienteCancelar(null)}
      />
    </div>
  );
}
